// COPIED FILE - do not edit here.
// Master: _shared/ at the top level. Change it there, then run
// `node sync-shared.mjs`, or services will silently disagree about
// permissions and token formats.

/**
 * SERVER-ONLY. Imported as "@py/shared/service".
 *
 * The two things every independently deployed service needs and none of them
 * had: a startup contract, and a clean way to stop.
 */
import type { Server } from "node:http";

/* ------------------------- 1. the startup contract ------------------------- */

export interface ConfigSpec {
  /** must be present or the service refuses to start */
  required: string[];
  /** absent is survivable, but say which feature stops working */
  optional?: Record<string, string>;
  /** shown in the boot log so a misconfigured deploy is obvious */
  show?: string[];
}

/**
 * Fail at BOOT, not at the first request.
 *
 * Twice during development a service started happily with a missing shared
 * secret and only broke when somebody clicked something — once taking email
 * sign-in down because a chat secret was absent. A service that cannot do its
 * job should refuse to start and say exactly which variable is missing.
 */
export function requireConfig(serviceName: string, spec: ConfigSpec): void {
  const missing = spec.required.filter((k) => !process.env[k]);

  if (missing.length > 0) {
    console.error(`\n[${serviceName}] cannot start — missing configuration:\n`);
    for (const key of missing) console.error(`    ${key}`);
    console.error(`\n  Set them in the environment (or .env) and start again.\n`);
    process.exit(1);
  }

  for (const [key, consequence] of Object.entries(spec.optional ?? {})) {
    if (!process.env[key]) {
      console.warn(`[${serviceName}] ${key} is not set — ${consequence}`);
    }
  }

  for (const key of spec.show ?? []) {
    const value = process.env[key];
    if (value) console.log(`[${serviceName}] ${key}=${value}`);
  }
}

/** Secrets must never be printed. Show enough to compare, not enough to use. */
export function fingerprint(secret: string | undefined): string {
  if (!secret) return "(not set)";
  return `${secret.slice(0, 3)}…${secret.slice(-3)} (${secret.length} chars)`;
}

/* --------------------------- 2. stopping cleanly --------------------------- */

/**
 * A container is stopped with SIGTERM and killed shortly after. Without this a
 * deploy cuts requests off mid-flight and leaves database connections dangling.
 *
 * Order matters: stop accepting new work, let what is in flight finish, then
 * release resources.
 */
export function gracefulShutdown(
  serviceName: string,
  server: Server,
  release?: () => Promise<void>,
  graceMs = 10_000
): void {
  let closing = false;

  const stop = async (signal: string) => {
    if (closing) return; // a second Ctrl-C should not race the first
    closing = true;
    console.log(`[${serviceName}] ${signal} received — finishing in-flight requests`);

    const forced = setTimeout(() => {
      console.error(`[${serviceName}] still busy after ${graceMs}ms — exiting anyway`);
      process.exit(1);
    }, graceMs);
    forced.unref();

    server.close(async () => {
      try {
        await release?.();
      } catch (err) {
        console.error(`[${serviceName}] cleanup failed:`, err);
      }
      clearTimeout(forced);
      console.log(`[${serviceName}] stopped cleanly`);
      process.exit(0);
    });
  };

  process.on("SIGTERM", () => void stop("SIGTERM"));
  process.on("SIGINT", () => void stop("SIGINT"));

  // A crashed service that keeps accepting traffic is worse than one that dies.
  process.on("unhandledRejection", (reason) => {
    console.error(`[${serviceName}] unhandled rejection:`, reason);
  });
  process.on("uncaughtException", (err) => {
    console.error(`[${serviceName}] uncaught exception — shutting down:`, err);
    void stop("uncaughtException");
  });
}
